import React, { useContext, useEffect, useState } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { UserContext, IUserContext } from 'contexts/user';
import DetailsTable from 'components/details-table';
import formatCurrency from 'utils/format-currency';
import textTransform from 'utils/text-transform';
import { ProvinceOrStateCode, provinceOrStateNameFromCode } from 'utils/province_or_state';
import { capitalize } from '@zoocasa/node-kit/strings/capitalize';
import styles from './style.module.scss';
import { getApproxAge } from 'utils/approx-age';
import { TryScrambleText } from 'utils/text-scrambler';
import { translate } from 'utils/translations';
import { testIds, listingPageModalIds } from 'constants/test-constants';
import { sanitizeTestid } from 'utils/stringSanitization';
import ListingAgent from '../listing-agent';

import type Listing from 'data/listing';

type Options = ({
  label: string;
  value?: string | number | boolean | null;
} | null)[];

interface Props {
  listing: Listing;
  isVisible: boolean;
  shouldScramble: boolean;
}

export default function KeyFacts({ listing, isVisible, shouldScramble }: Props) {
  const { isCondo, availableAt, isAvailable, daysOnMarket, lotSize, mlsNum,
    maintenanceFees, taxes, isRental, miscellaneous, priceBySqft, getStreet, city, province } = listing;
  const { isAuthenticated, siteLocation } = useContext(UserContext) as IUserContext;
  const taxesAsCurrency = taxes ? formatCurrency(taxes) : null;
  const maintenanceFeesAsCurrency = maintenanceFees ? formatCurrency(maintenanceFees) : null;
  const street = getStreet(isAuthenticated);
  const [propertyAddress, setPropertyAddress] = useState('');

  const getDetails = () => {
    const { approxAge } = miscellaneous || {};
    const properties = buildOptions(listing, 'type', 'levels', 'sqftLabel:Size', 'garage');
    const remainingProperties = [
      !isRental && isCondo ? { label: translate('Maintenance Fees', siteLocation), value: maintenanceFeesAsCurrency } : null,
      !isRental ? { label: 'Taxes', value: taxesAsCurrency } : null,
      isRental ? { label: 'Date Available', value: availableAt } : null,
      isAvailable ? { label: 'Days Active', value: daysOnMarket } : null,
      !isCondo ? { label: 'Lot Size', value: lotSize } : null,
      { label: 'Approx. Age', value: approxAge ? getApproxAge(approxAge.toString()) : null },
      !isRental && priceBySqft ? { label: 'Avg. Price Per Sqft', value: priceBySqft } : null,
      { label: 'MLS® Number', value: mlsNum || '' },
    ];

    return properties.concat(remainingProperties);
  };

  const buildOptions = (keyPrefix: Record<string, any> | null, ...properties: string[]): Options => {
    return properties.map(property => {
      const [name, label] = property.split(':');
      const key = keyPrefix && keyPrefix[name];
      let value;
      if (key === true) {
        value = 'Yes';
      } else if (key === false) {
        value = 'No';
      } else {
        value = key;
      }
      return {
        label: label || capitalize(textTransform(name, 'deCamelize')),
        value,
      };
    });
  };

  useEffect(() => {
    setPropertyAddress(`${street}, ${city}${city ? ',' : ''} ${province ? provinceOrStateNameFromCode(province.toLowerCase() as ProvinceOrStateCode) : ''}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.component}>
      <DetailsTable isPrivate={!isVisible}>
        <section>
          {getDetails().map((field, index) => {
            if (field) {
              return (
                <div key={index}>
                  <span>{field.label}</span>
                  &nbsp;
                  <span data-testid={sanitizeTestid(field.label) + testIds.keyFacts}>{TryScrambleText(field.value, shouldScramble)}</span>
                </div>
              );
            }
          })}
        </section>
        <p className={buildClassName((!isVisible) && 'blur', styles.address)} data-testid={listingPageModalIds.keyFactsPropertyAddress}><span>Property Address:</span>&nbsp;{TryScrambleText(propertyAddress, shouldScramble)}</p>
        <p className={styles.disclaimer}>Average square foot calculated using Size range provided by listing agent.</p>
      </DetailsTable>
      <ListingAgent listing={listing} />
    </div>
  );
}
